import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ApiResponseContainer } from "../models/api";
import { AxiosAuth } from "../core/axios";
import { StorageAuth } from "../core/localStorage";
import { useAppDispatch } from "../core/redux";
import { debounce } from "../utils";
import { showPaginatedTableToast } from "../components/toast.component";

export interface IPaginationSearchFilter {
  apiRoute: string;
  requestType?: "POST" | "GET";
  customParams: {
    CompanyId?: string;
    PageNumber?: number;
    PageSize?: number;
    StartDate?: string | null;
    EndDate?: string | null;
    SearchKey?: string;
    NextPageToken?: null | string;
    AppointmentStatus?: string | null;
    PatientStatus?: string | null;
    Tasks?: string[];
  };
}

interface IReduxAction<T> {
  payload: T;
  type: string;
}

interface IReduxActions {
  setNextPageToken: (payload: string | null) => IReduxAction<typeof payload>;
  setIsLoading: (payload: boolean) => IReduxAction<typeof payload>;
  setRefetch: (payload: boolean) => IReduxAction<typeof payload>;
}

interface IApiResponse {
  nextPageToken: null | string;
}

type TypeParams = IPaginationSearchFilter["customParams"];

export function useTablePaginationSearchFilter<
  T extends IApiResponse,
  A extends IReduxActions
>(
  tableAction: A,
  { customParams, apiRoute, requestType }: IPaginationSearchFilter
) {
  const dispatch = useAppDispatch();
  const [apiData, setApiData] = useState<T | null>();
  const isFetchingRef = useRef(true);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const params: TypeParams = useMemo(() => {
    const data: TypeParams = customParams;
    data.CompanyId = customParams?.CompanyId || StorageAuth.CompanyId;
    data.PageNumber = customParams?.PageNumber || 1;
    data.PageSize = customParams?.PageSize || 10;
    !customParams.StartDate && delete data.StartDate;
    !customParams.EndDate && delete data.EndDate;
    !customParams.SearchKey && delete data.SearchKey;
    !customParams?.Tasks?.length && delete data?.Tasks;
    !customParams?.AppointmentStatus && delete data?.AppointmentStatus;
    !customParams?.PatientStatus && delete data?.PatientStatus;
    data.NextPageToken = `${customParams?.NextPageToken || null}`;
    return data;
  }, [customParams]);

  const refetch = useCallback(() => {
    setShouldRefetch(true);
  }, []);

  const getData = useCallback(async () => {
    dispatch(tableAction.setIsLoading(true));
    try {
      const ApiRequest =
        requestType !== "POST" ? AxiosAuth.get : AxiosAuth.post;
      const response = await ApiRequest<ApiResponseContainer<T>>(
        apiRoute,
        requestType !== "POST" ? { params } : { ...params }
      );
      if (response.data.isSuccess) {
        setApiData(response.data.data!);
        dispatch(
          tableAction.setNextPageToken(
            response.data.data?.nextPageToken! || null
          )
        );
      } else {
        showPaginatedTableToast("error", response.data.message);
      }
    } catch (error) {
      showPaginatedTableToast("error");
    }
    isFetchingRef.current = false;
    dispatch(tableAction.setIsLoading(false));
    dispatch(tableAction.setRefetch(false));
    setShouldRefetch(false);
  }, [params, apiRoute, dispatch, tableAction, requestType]);

  useEffect(() => {
    if (params?.SearchKey) {
      debounce(getData);
    } else {
      getData();
    }
  }, [params?.SearchKey, getData]);

  useEffect(() => {
    shouldRefetch && getData();
  }, [shouldRefetch, getData]);

  return {
    apiData,
    isFetching: isFetchingRef.current,
    refetch,
  };
}
